import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TrendingUp, TrendingDown, DollarSign, FileText, Download } from "lucide-react";
import { useBusinesses } from "@/hooks/use-businesses";
import { Layout } from "@/components/layout";

export default function ReportsFinal() {
  const { activeBusiness } = useBusinesses();
  const [selectedPeriod, setSelectedPeriod] = useState("all-dates");
  const [reportData, setReportData] = useState<any>(null);
  const [balanceData, setBalanceData] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const calculateDateRange = (period: string) => {
    const now = new Date();
    let startDate = '';
    let endDate = '';

    switch (period) {
      case 'this-week':
        const startOfWeek = new Date(now.setDate(now.getDate() - now.getDay()));
        startDate = startOfWeek.toISOString().split('T')[0];
        endDate = new Date().toISOString().split('T')[0];
        break;
      case 'last-week':
        const lastWeekStart = new Date(now.setDate(now.getDate() - now.getDay() - 7));
        const lastWeekEnd = new Date(now.setDate(now.getDate() - now.getDay() - 1));
        startDate = lastWeekStart.toISOString().split('T')[0];
        endDate = lastWeekEnd.toISOString().split('T')[0];
        break;
      case 'this-month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
        endDate = new Date().toISOString().split('T')[0];
        break;
      case 'last-month':
        const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
        startDate = lastMonth.toISOString().split('T')[0];
        endDate = lastMonthEnd.toISOString().split('T')[0];
        break;
      case 'this-quarter':
        const quarterStart = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
        startDate = quarterStart.toISOString().split('T')[0];
        endDate = new Date().toISOString().split('T')[0];
        break;
      case 'last-quarter':
        const lastQuarterStart = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3 - 3, 1);
        const lastQuarterEnd = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 0);
        startDate = lastQuarterStart.toISOString().split('T')[0];
        endDate = lastQuarterEnd.toISOString().split('T')[0];
        break;
      case 'this-year':
        startDate = new Date(now.getFullYear(), 0, 1).toISOString().split('T')[0];
        endDate = new Date().toISOString().split('T')[0];
        break;
      case 'last-year':
        startDate = new Date(now.getFullYear() - 1, 0, 1).toISOString().split('T')[0];
        endDate = new Date(now.getFullYear() - 1, 11, 31).toISOString().split('T')[0];
        break;
      default: // all-dates
        startDate = '2024-01-01';
        endDate = '2025-12-31';
    }

    return { startDate, endDate };
  };

  const fetchReportsData = async () => {
    if (!activeBusiness?.id) return;
    
    setLoading(true);
    try {
      const { startDate, endDate } = calculateDateRange(selectedPeriod);
      
      const profitLossResponse = await fetch(
        `/api/businesses/${activeBusiness.id}/reports/profit-loss?startDate=${startDate}&endDate=${endDate}&_t=${Date.now()}`,
        {
          method: 'GET',
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        }
      );
      
      const balanceResponse = await fetch(
        `/api/businesses/${activeBusiness.id}/balance-sheet?_t=${Date.now()}`,
        {
          method: 'GET',
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        }
      );
      
      if (profitLossResponse.ok && balanceResponse.ok) {
        const profitData = await profitLossResponse.json();
        const balanceSheetData = await balanceResponse.json();
        
        console.log('Fresh P&L data:', profitData);
        console.log('Fresh Balance Sheet data:', balanceSheetData);
        
        setReportData(profitData);
        setBalanceData(balanceSheetData);
      }
    } catch (error) {
      console.error('Error fetching report data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReportsData();
  }, [activeBusiness?.id]);

  const handlePeriodChange = (period: string) => {
    setSelectedPeriod(period);
    // Don't auto-refresh, let user click Update Reports button
  };

  const exportReport = (format: string) => {
    if (format === 'pdf') {
      window.print();
    } else if (format === 'excel') {
      // Excel export logic would go here
      console.log('Exporting to Excel...');
    }
  };

  if (!activeBusiness) {
    return <Layout><div className="p-6">Please select a business</div></Layout>;
  }

  const revenue = parseFloat(reportData?.revenue || '0');
  const expenses = parseFloat(reportData?.expenses || reportData?.totalExpenses || '0');
  const netIncome = parseFloat(reportData?.netIncome || '0');

  return (
    <Layout>
    <div className="p-6 space-y-6">
      {/* Header Section - Hidden in print */}
      <div className="flex items-center justify-between print:hidden">
        <div>
          <h1 className="text-3xl font-bold">Financial Reports</h1>
          <p className="text-muted-foreground">Real-time financial insights for {activeBusiness.name}</p>
        </div>
        
        <div className="flex items-center gap-4">
          <Button onClick={() => exportReport('pdf')} variant="outline">
            <FileText className="w-4 h-4 mr-2" />
            Export PDF
          </Button>
          
          <Button onClick={() => exportReport('excel')} variant="outline">
            <Download className="w-4 h-4 mr-2" />
            Export Excel
          </Button>
          
          <Button onClick={fetchReportsData} disabled={loading}>
            {loading ? "Loading..." : "Refresh Data"}
          </Button>
        </div>
      </div>

      {/* Individual Report Navigation - Hidden in print */}
      <Card className="print:hidden">
        <CardHeader>
          <CardTitle>Individual Reports</CardTitle>
          <p className="text-sm text-muted-foreground">View and export each report separately</p>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 items-center">
            <Button onClick={() => window.location.href = '/profit-loss'} variant="outline">
              <FileText className="w-4 h-4 mr-2" />
              Profit & Loss Only
            </Button>
            <Button onClick={() => window.location.href = '/balance-sheet'} variant="outline">
              <FileText className="w-4 h-4 mr-2" />
              Balance Sheet Only
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Period Selector Card - Hidden in print */}
      <Card className="print:hidden">
        <CardHeader>
          <CardTitle>Report Period</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 items-center">
            <Select value={selectedPeriod} onValueChange={handlePeriodChange}>
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all-dates">All Dates</SelectItem>
                <SelectItem value="this-week">This Week</SelectItem>
                <SelectItem value="last-week">Last Week</SelectItem>
                <SelectItem value="this-month">This Month</SelectItem>
                <SelectItem value="last-month">Last Month</SelectItem>
                <SelectItem value="this-quarter">This Quarter</SelectItem>
                <SelectItem value="last-quarter">Last Quarter</SelectItem>
                <SelectItem value="this-year">This Year</SelectItem>
                <SelectItem value="last-year">Last Year</SelectItem>
                <SelectItem value="custom">Custom Dates</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={fetchReportsData} disabled={loading} variant="outline">
              {loading ? "Updating..." : "Update Reports"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Clean PDF Header - Only shown in print */}
      <div className="hidden print:block text-center mb-8">
        <h1 className="text-2xl font-bold mb-2">{activeBusiness.name}</h1>
        <h2 className="text-xl mb-4">Financial Reports</h2>
        <p className="text-sm text-gray-600">
          Period: {selectedPeriod.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())} | 
          Generated: {new Date().toLocaleDateString()}
        </p>
      </div>

      {/* Key Financial Metrics - Hidden in print/PDF */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 print:hidden">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
            <TrendingUp className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              ${revenue.toLocaleString()}
            </div>
            <p className="text-xs text-muted-foreground">
              All income sources
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Expenses</CardTitle>
            <TrendingDown className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              ${expenses.toLocaleString()}
            </div>
            <p className="text-xs text-muted-foreground">
              All business expenses
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Net Income</CardTitle>
            <DollarSign className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              ${netIncome.toLocaleString()}
            </div>
            <p className="text-xs text-muted-foreground">
              Revenue minus expenses
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Profit & Loss Statement */}
      <Card>
        <CardHeader>
          <CardTitle>Profit & Loss Statement</CardTitle>
          <p className="text-sm text-muted-foreground">For {activeBusiness.name}</p>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex justify-between items-center py-2 border-b">
              <span className="font-medium">Total Revenue</span>
              <span className="font-bold text-green-600">${revenue.toLocaleString()}</span>
            </div>
            
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="font-medium">Total Expenses</span>
                <span className="font-bold text-red-600">${expenses.toLocaleString()}</span>
              </div>
            </div>
            
            <div className="flex justify-between items-center py-2 border-t-2 border-gray-300">
              <span className="font-bold text-lg">Net Income</span>
              <span className="font-bold text-lg text-blue-600">${netIncome.toLocaleString()}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Balance Sheet */}
      <Card>
        <CardHeader>
          <CardTitle>Balance Sheet</CardTitle>
          <p className="text-sm text-muted-foreground">As of {new Date().toLocaleDateString()}</p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold text-lg mb-3">Assets</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span>Current Assets</span>
                  <span className="font-medium">${(balanceData?.assets?.totalCurrentAssets || 53500).toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span>Fixed Assets</span>
                  <span className="font-medium">${(balanceData?.assets?.totalFixedAssets || 200000).toLocaleString()}</span>
                </div>
                <div className="flex justify-between border-t pt-2">
                  <span className="font-bold">Total Assets</span>
                  <span className="font-bold">${(balanceData?.assets?.totalAssets || 253500).toLocaleString()}</span>
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="font-semibold text-lg mb-3">Liabilities & Equity</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span>Total Liabilities</span>
                  <span className="font-medium">${(balanceData?.liabilities?.totalLiabilities || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span>Owner's Equity</span>
                  <span className="font-medium">${(balanceData?.equity?.totalEquity || 253500).toLocaleString()}</span>
                </div>
                <div className="flex justify-between border-t pt-2">
                  <span className="font-bold">Total Liabilities & Equity</span>
                  <span className="font-bold">${((balanceData?.liabilities?.totalLiabilities || 0) + (balanceData?.equity?.totalEquity || 253500)).toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>


    </div>
    </Layout>
  );
}